import { FC, useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/akar-icons_globe.svg'
import { useGetKeywordVariationsQuery } from 'src/reactQuery/useGetKeywordVariationsQuery'
import { DEFAULT_VARIATION_LANGUAGES } from 'src/constants/variationLanguages'
import { AddLanguageModal } from './AddLanguageModal'
import styles from './suggestedVariations.module.scss'

interface SuggestedVariationsProps {
  /** Previously-saved selected languages (edit flow); seeds the chips once. */
  initialLanguages?: string[]
  /** Previously-saved selected variations (edit flow); seeds the selection once. */
  initialSelected?: string[]
  keyword?: string
  /** Reports the selected languages up so they can be persisted with the keyword. */
  onLanguagesChange?: (languages: string[]) => void
  /** Reports the current selection up so it can be persisted with the keyword. */
  onSelectedChange?: (selected: string[]) => void
}

// Fixed skeleton widths so the placeholder tags don't jiggle between renders.
const SKELETON_WIDTHS = [80, 95, 70, 110, 85, 100]

const unique = (items: string[]) => Array.from(new Set(items))

/**
 * Suggested keyword variations for a single keyword, generated per selected
 * language. The user picks languages (chips + "Add Language" modal); variations
 * for all selected languages are merged into one deselectable list. Generation
 * only runs for languages not already saved on the keyword, so editing reuses
 * the saved set without re-generating. Selected variations and languages are
 * reported upward to be persisted with the keyword.
 */
export const SuggestedVariations: FC<SuggestedVariationsProps> = ({
  initialLanguages,
  initialSelected,
  keyword,
  onLanguagesChange,
  onSelectedChange,
}) => {
  const trimmedKeyword = keyword?.trim() ?? ''

  // Saved values seed the component once and are captured in refs so that
  // edit-mode re-renders (which feed these props back in) don't reset state.
  const savedVariationsRef = useRef<string[]>(initialSelected ?? [])
  const savedLanguagesRef = useRef<string[]>(initialLanguages ?? [])

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    initialLanguages ?? []
  )
  const [deselectedVariations, setDeselectedVariations] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Only languages with nothing saved yet trigger generation; saved languages
  // are already covered by the saved variations, so editing never re-generates.
  const languagesToFetch = selectedLanguages.filter(
    (language) => !savedLanguagesRef.current.includes(language)
  )

  const { variations: fetchedVariations, isLoading } =
    useGetKeywordVariationsQuery(trimmedKeyword, languagesToFetch)

  // Saved selections stay visible alongside freshly generated ones.
  const displayedVariations = unique([
    ...savedVariationsRef.current,
    ...fetchedVariations,
  ])

  // Everything is selected by default; the user opts items out (deselect).
  const selectedVariations = displayedVariations.filter(
    (variation) => !deselectedVariations.includes(variation)
  )

  const selectedKey = selectedVariations.join(' ')
  useEffect(() => {
    onSelectedChange?.(selectedVariations)
    // eslint-disable-next-line
  }, [selectedKey])

  const languagesKey = selectedLanguages.join(' ')
  useEffect(() => {
    onLanguagesChange?.(selectedLanguages)
    // eslint-disable-next-line
  }, [languagesKey])

  if (!trimmedKeyword) {
    return null
  }

  const chipLanguages = unique([
    ...DEFAULT_VARIATION_LANGUAGES,
    ...selectedLanguages,
  ])

  const isGenerating = isLoading && displayedVariations.length === 0
  const hasLanguage = selectedLanguages.length > 0
  const allSelected =
    selectedVariations.length === displayedVariations.length &&
    displayedVariations.length > 0

  const toggleLanguage = (language: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(language)
        ? prev.filter((item) => item !== language)
        : [...prev, language]
    )
  }

  const toggleVariation = (variation: string) => {
    setDeselectedVariations((prev) =>
      prev.includes(variation)
        ? prev.filter((item) => item !== variation)
        : [...prev, variation]
    )
  }

  const toggleSelectAll = () => {
    setDeselectedVariations(allSelected ? [...displayedVariations] : [])
  }

  return (
    <div className={styles.variationsSection}>
      <div className={styles.variationsInRow}>
        <div className={styles.variationsInLeft}>
          <GlobeIcon className={styles.globe} />
          <span className={styles.variationsInLabel}>Variations in:</span>
        </div>

        <div className={styles.languagePills}>
          {chipLanguages.map((language) => (
            <button
              aria-pressed={selectedLanguages.includes(language)}
              className={classNames(
                styles.languagePill,
                selectedLanguages.includes(language) &&
                  styles.languagePillActive
              )}
              key={language}
              onClick={() => toggleLanguage(language)}
              type="button"
            >
              {language}
            </button>
          ))}

          <button
            className={styles.morePill}
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            + more
          </button>
        </div>
      </div>

      <div className={styles.variationsLabelRow}>
        <span className={styles.variationsLabel}>Suggested variations</span>
        {!hasLanguage && (
          <span className={styles.selectLanguageBadge}>
            select language above
          </span>
        )}
      </div>

      {isGenerating ? (
        <div className={styles.variationsGenerating}>
          <div className={styles.variationsGeneratingHeader}>
            <ReloadIcon
              className={styles.variationsLoadingIcon}
              height={22}
              width={22}
            />
            <div className={styles.variationsGeneratingTexts}>
              <div className={styles.variationsGeneratingTitle}>
                Generated keyword variations
              </div>
              <div className={styles.variationsGeneratingSubtitle}>
                We are analyzing your input to suggest relevant variations
              </div>
            </div>
          </div>
          <div className={styles.variationsSkeletonContainer}>
            {SKELETON_WIDTHS.map((width, i) => (
              <div
                className={styles.variationsSkeletonTag}
                key={i}
                style={{ width: `${width}px` }}
              />
            ))}
          </div>
        </div>
      ) : displayedVariations.length > 0 ? (
        <>
          <div className={styles.variationsContainer}>
            <button
              className={styles.variationsSelectAll}
              onClick={toggleSelectAll}
              type="button"
            >
              <svg
                fill="none"
                height="12"
                viewBox="0 0 12 12"
                width="12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect fill="white" height="12" rx="2" width="12" />
                {allSelected && (
                  <path
                    d="M3 6L5 8L9 4"
                    stroke="#8080FF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                )}
              </svg>
              Select All
            </button>

            {displayedVariations.map((variation) => {
              const isSelected = !deselectedVariations.includes(variation)
              return (
                <div
                  className={
                    isSelected
                      ? styles.variationTagSelected
                      : styles.variationTag
                  }
                  key={variation}
                  onClick={() => toggleVariation(variation)}
                >
                  <span>{variation}</span>
                  {isSelected && (
                    <button
                      className={styles.variationTagRemove}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleVariation(variation)
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className={styles.variationsEmpty}>
          <div className={styles.variationsEmptyHeading}>
            <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
              <path
                d="M8 10h8M9 14h6M7 3h10a2 2 0 0 1 2 2v14l-3-2-4 2-4-2-3 2V5a2 2 0 0 1 2-2Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            <div className={styles.variationsEmptyTitle}>
              {hasLanguage
                ? 'No suggested variations found'
                : 'No variations to display'}
            </div>
          </div>
          <div className={styles.variationsEmptySubtitle}>
            {hasLanguage
              ? 'Try another language or keyword.'
              : 'Suggested variations will appear here once you select a language above.'}
          </div>
        </div>
      )}

      <div className={styles.variationsNote}>
        * Variations do not count toward your keyword limit.
      </div>

      <AddLanguageModal
        isOpen={isModalOpen}
        onApply={(languages) => {
          setSelectedLanguages(languages)
          setIsModalOpen(false)
        }}
        onClose={() => setIsModalOpen(false)}
        selected={selectedLanguages}
      />
    </div>
  )
}

export default SuggestedVariations
